<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1>{{ $t('common.zone') }}</h1>
      <div class="details-component__header_right" v-if="!isDeleted() && zones.meta.total > 0">
        <router-link :to="{name: 'edit-zone', params: { id: selectedZoneId }}" class="link" v-if="selectedZoneId">
          {{ $t('common.change') }}
          <i class="icon icon--arrow"></i>
        </router-link>
        <a v-if="isSuperAdmin()" v-on:click="showDeletingConfirmation" class="link link--orange">{{ $t('common.delete') }}&nbsp;<i class="icon icon--cross"></i></a>
      </div>
    </div>
    <div class="details-component__content content">
      <div class="content__cell">
        <table class="details-table no-col-padding label-r-padding fixed-table label-col-wide-150">
          <tr class="details-table__row">
            <td class="details-table__col details-table__col_label">{{ $t('zones.code') }}</td>
            <td class="details-table__col tenant__name">{{ zone.code }}</td>
          </tr>
          <tr class="details-table__row">
            <td class="details-table__col details-table__col_label">{{ $t('common.address') }}</td>
            <td class="details-table__col">{{ zone.address }}</td>
          </tr>
          <tr class="details-table__row">
            <td class="details-table__col details-table__col_label">{{ $t('zones.eps_zone_id') }}</td>
            <td class="details-table__col">{{ zone.epsZoneId }}</td>
          </tr>
          <tr class="details-table__row">
            <td class="details-table__col details-table__col_label">{{ $t('zones.parking_spaces') }}</td>
            <td class="details-table__col">{{ zone.parking_spaces }}</td>
          </tr>
        </table>
        <table class="details-table no-col-padding label-r-padding fixed-table" v-if="user.autopayEnabled && zone.autopay_settings">
          <tr class="details-table__row">
            <td colspan="2"><h3>{{ $t('autopay.settings')}}</h3></td>
          </tr>
          <tr class="details-table__row">
            <td class="details-table__col details-table__col_label">{{ $t('autopay.client_secret') }}</td>
            <td class="details-table__col">{{ zone.autopay_settings.client_secret }}</td>
          </tr>
          <tr class="details-table__row">
            <td class="details-table__col details-table__col_label">{{ $t('autopay.client_id') }}</td>
            <td class="details-table__col">{{ zone.autopay_settings.client_id }}</td>
          </tr>
          <tr class="details-table__row">
            <td class="details-table__col details-table__col_label">{{ $t('autopay.permit_definition') }}</td>
            <td class="details-table__col">{{ zone.autopay_settings.permit_definition }}</td>
          </tr>
        </table>
        <table class="details-table no-col-padding label-r-padding fixed-table">
          <tr class="details-table__row" v-if="isAutloIntegrationEnabled()">
            <td class="details-table__col details-table__col_label">{{$t('zones.autlo_zone')}}</td>
            <td class="details-table__col">{{ zone.isAutloZone == true ? $t('common.yes') : $t('common.no') }}</td>
          <tr class="details-table__row">
            <td class="details-table__map-container" colspan="2">
              <div id='zone-map-dom'></div>
            </td>
          </tr>
        </table>
      </div>
      <ConfirmationModal v-if="showConfirmationModal" @cancel="cancelZoneDelete" @confirmed="deleteZone">
        <h1 slot="header">{{ $t('common.deleting') }}: {{ zone.code }} </h1>
        <p slot="body">{{ $t('zones.confirm_delete') }}</p>
      </ConfirmationModal>

      <WarningModal v-if="showWarningModal" @close="closeWarningModal">
        <h1 slot="header">{{ zoneWarningHeader }}</h1>
        <p slot="body">{{ zoneWarningBody }}</p>
      </WarningModal>

    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import L from 'leaflet';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';

export default {
  components: {
    ConfirmationModal,
    WarningModal,
  },
  computed: mapGetters({
    zone: 'zones/zone/zone',
    selectedZoneId: 'zones/zone/selectedZoneId',
    zoneError: 'zones/zone/error',
    coordinates: 'zones/zone/coordinates',
    zones: 'landlord/zones/zones',
    user: 'user/user/user',
  }),
  created() {
    this.fetchZone();
  },
  watch: {
    $route: 'fetchZone',
  },
  methods: {
    isDeleted() {
      this.$emit('isDeleted', this.zone.isDeleted);
      return this.zone.isDeleted;
    },
    fetchZone() {
      if (this.$route.params.id && this.user.data) {
        this.$store.dispatch('zones/zone/fetch', { id: this.$route.params.id, user: this.user }).then(() => {
          this.initMap();
          this.updateMap();
        });
      } else {
        this.$store.commit(`zones/zone/${mTypes.CLEAR_STATE}`);
        this.updateMap();
      }
    },
    initMap() {
      Vue.nextTick(() => {
        this.mapInstance = new mapboxgl.Map({
          container: 'zone-map-dom',
          style: `${process.env.MAPS_URL}/styles/${this.style}/style.json`,
          hash: false,
          center: [0, 0],
          zoom: 15,
        });
        this.mapInstance.addControl(new mapboxgl.NavigationControl());
      });
    },
    showDeletingConfirmation() {
      if (this.zone.products > 0) {
        this.zoneWarningHeader = this.$t('zones.cannot_remove');
        this.zoneWarningBody = this.$t('zones.products_added');
        this.showWarningModal = true;
      } else {
        this.showConfirmationModal = true;
      }
    },
    deleteZone() {
      this.showConfirmationModal = false;
      this.$store.dispatch('zones/zone/delete', { id: this.selectedZoneId, user: this.user }).then(() => {
        if (this.zoneError) {
          this.zoneWarningHeader = this.$t('common.error');
          this.zoneWarningBody = this.$t('zones.delete_error');
          this.showWarningModal = true;
        } else {
          this.$store.commit(`landlord/zones/${mTypes.REMOVE_DATA}`, this.selectedZoneId);
          this.$router.push({ name: 'zones' });
        }
      });
    },
    cancelZoneDelete() {
      this.showConfirmationModal = false;
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.$store.commit(`zones/zone/${mTypes.DATA_ERROR}`, null);
    },
    cleanupCoordinates(coordinates) {
      let data = {};

      if (coordinates) {
        if (Array.isArray(coordinates) && coordinates[0] && coordinates[0].type === 'Feature') {
          data = {
            type: 'FeatureCollection',
            features: coordinates,
          };
        } else if (coordinates === Object(coordinates) && coordinates.type === 'Feature') {
          data = {
            type: 'FeatureCollection',
            features: [coordinates],
          };
        } else if (Array.isArray(coordinates) && coordinates[0] && coordinates[0].type === 'FeatureCollection') {
          data = coordinates[0];
        } else if (coordinates === Object(coordinates) && coordinates.type === 'FeatureCollection') {
          data = coordinates;
        }
      }

      return {
        id: 'zone-outline',
        type: 'fill',
        source: {
          type: 'geojson',
          data,
        },
        layout: {},
        paint: {
          'fill-color': '#088',
          'fill-opacity': 0.8,
        },
      };
    },
    setCenter() {
      if (this.zone && this.zone.center && this.zone.center[0] > 0 && this.zone.center[1] > 0) {
        this.mapInstance.setCenter(this.zone.center);
        return;
      } else if (this.coordinates) {
        const bounds = L.geoJSON(this.coordinates).getBounds();
        if (bounds.isValid()) {
          const center = bounds.getCenter();
          if (center) {
            this.mapInstance.setCenter(center);
            return;
          }
        }
      }
      //
      this.mapInstance.setCenter([24.707, 59.412]);
    },
    updateMap() {
      if (!this.mapInstance) {
        this.initMap();
      }
      Vue.nextTick(() => {
        // Set Center and zoom
        this.setCenter();
        if (this.zone && this.zone.zoom) {
          this.mapInstance.zoomTo(this.zone.zoom);
        } else {
          this.mapInstance.zoomTo(15);
        }
        // Add parking outline
        this.mapInstance.on('load', () => {
          this.mapInstance.addLayer(this.cleanupCoordinates(this.coordinates));
        });
      });
    },
  },
  data() {
    return {
      mapInstance: null,
      showConfirmationModal: false,
      showWarningModal: false,
      zoneWarningBody: null,
      zoneWarningHeader: null,
      style: 'klokantech-basic',
    };
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/variables.scss';
@import '~styles/blocks/details.scss';

.link--orange {
  .icon--plus {
    position: relative;
    top: 1px;
    margin: 0;
    font-weight: 900;
  }
}

#zone-map-dom {
  width: 100%;
  height: 500px;
}

</style>



// WEBPACK FOOTER //
// src/components/zones/ZoneDetails.vue